/**
 * Vérification d'une copie de forme (`copy_format`, #1869) : la forme relue sur
 * chaque cible dans le fichier exporté après l'écriture, confrontée à celle
 * que la copie a reprise du paragraphe modèle (`copieDeForme.ts`).
 *
 * Module pur. Chaque réglage copié est jugé par le juge de sa famille —
 * `verificationDeParagraphe.ts` pour un réglage de paragraphe,
 * `verificationDeForme.ts` pour un réglage de texte — avec, pour valeur
 * demandée, celle lue sur le modèle. Ce module n'ajoute que les deux réglages
 * qu'une copie est seule à poser — le style nommé et la bordure par ses traits
 * — et le compte de ce qui, sur une cible, n'a pas été écrit exprès.
 */

import {
  CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX,
  CANVAS_FORMAT_DIFFERENCE_TEXT_MAX,
  CANVAS_PARAGRAPH_BORDER_SIDES,
  CANVAS_TEXT_FORMAT_SETTING_KEYS,
  type CanvasFormatSettingKey,
  type CanvasFormatSettingReceipt,
  type CanvasFormatSettingValue,
  type CanvasFormatUnreadableReason,
  type CanvasParagraphBorderSide,
} from '@hector/shared-types';
import { copieUneFormeDeTexte, ecartsAuBrut, REGLAGES_DE_PARAGRAPHE_COPIES, type FormeCopiee, type InterligneEnPoints } from './copieDeForme';
import { huitiemesEnPt, vingtiemesEnPt, type FormeDeParagraphe, type TraitDeBordure } from '@hector/docx-forme';
import { memeTrait } from './reglagesDeParagraphe';
import { REGLAGE_QUI_EFFACE } from './remiseAZero';
import { verifierLaFormeEcrite, type RelectureDeLActe } from './verificationDeForme';
import { verifierLaFormeDeParagraphe } from './verificationDeParagraphe';

/** Une cible de la copie dont toutes les écritures prévues sont faites (ou qui n'en demandait pas). */
export interface CibleCopiee {
  nodeId: string;
  /** Faux pour un paragraphe sans texte : aucun réglage de texte n'y est jugé. */
  avecTexte: boolean;
  /**
   * Les réglages copiés que la copie n'a PAS écrits sur cette cible, exprès,
   * avec la raison : ils n'y sont pas jugés, et comptent comme non jugés.
   */
  nonEcrits: Partial<Record<CanvasFormatSettingKey, CanvasFormatUnreadableReason>>;
}

/**
 * La bordure en clair, telle que le reçu la nomme : `none`, ou chaque côté
 * tracé avec son type, son épaisseur, sa couleur et son écart au texte.
 */
export function decrireLesTraits(traits: Readonly<Record<CanvasParagraphBorderSide, TraitDeBordure | null>>): string {
  const traces = CANVAS_PARAGRAPH_BORDER_SIDES.flatMap((cote) => {
    const trait = traits[cote];
    if (trait === null) return [];
    const couleur = trait.couleur === 'auto' ? 'auto' : `#${trait.couleur.toUpperCase()}`;
    return [`${cote}: ${trait.type} ${huitiemesEnPt(trait.huitiemes)}pt ${couleur}, ${trait.ecart}pt from text`];
  });
  return traces.length === 0 ? 'none' : traces.join('; ');
}

/** Un interligne fixé en points, en clair : sa règle et sa hauteur (`exact 14pt`). */
export const decrireLInterligneEnPoints = ({ line, lineRule }: InterligneEnPoints): string => `${lineRule} ${vingtiemesEnPt(line)}pt`;

/** Les éléments de `w:rPr` des sept réglages de texte et leurs jumeaux d'écriture complexe : ils portent la forme copiée, pas une forme « autre ». */
const ELEMENTS_DES_SEPT: ReadonlySet<string> = new Set(['b', 'i', 'u', 'highlight', 'color', 'rFonts', 'sz', 'bCs', 'iCs', 'szCs']);

/** Ce que la relecture dit d'une cible pour un réglage propre à la copie. */
type Verdict = { issue: 'conforme' } | { issue: 'different'; lu: CanvasFormatSettingValue } | { issue: 'non_lisible' };

/** Juge, cible par cible, un réglage que seule la copie pose (style nommé, bordure par ses traits). */
function jugerParCible(
  setting: CanvasFormatSettingKey,
  requested: CanvasFormatSettingValue,
  relecture: RelectureDeLActe,
  cibles: ReadonlyArray<{ nodeId: string }>,
  paragraphesNonEcrits: number,
  juger: (relu: FormeDeParagraphe) => Verdict,
): CanvasFormatSettingReceipt {
  const relus = 'acte' in relecture ? new Map(relecture.acte.paragraphes.map((lu) => [lu.nodeId, lu])) : undefined;
  const exemples: Array<{ nodeId: string; text?: string; read: CanvasFormatSettingValue }> = [];
  let ecarts = 0;
  let nonLus = 0;
  let raison: CanvasFormatUnreadableReason | undefined;
  const nonLu = (cause: CanvasFormatUnreadableReason, nombre = 1): void => {
    if (nombre === 0) return;
    nonLus += nombre;
    raison ??= cause;
  };
  for (const { nodeId } of cibles) {
    if ('nonRelu' in relecture) { nonLu(relecture.nonRelu); continue; }
    const relu = relus?.get(nodeId);
    if (relu === undefined) { nonLu('target_not_found_on_reread'); continue; }
    const verdict = juger(relu);
    if (verdict.issue === 'non_lisible') { nonLu('cascade_not_resolved'); continue; }
    if (verdict.issue === 'conforme') continue;
    ecarts += 1;
    if (exemples.length < CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX) {
      const debut = relu.passages.map((passage) => passage.texte).join('').slice(0, CANVAS_FORMAT_DIFFERENCE_TEXT_MAX);
      exemples.push({ nodeId, ...(debut === '' ? {} : { text: debut }), read: verdict.lu });
    }
  }
  nonLu('not_written', paragraphesNonEcrits);
  const [premier, ...autres] = exemples;
  return {
    setting,
    requested,
    outcome: ecarts > 0 ? 'different' : nonLus > 0 ? 'unreadable' : 'conforming',
    ...(premier === undefined ? {} : { different: { count: ecarts, examples: [premier, ...autres] } }),
    ...(raison === undefined ? {} : { unreadable: { count: nonLus, reason: raison } }),
  };
}

/** Ajoute à un reçu de réglage des cibles non jugées : il ne peut plus être dit conforme pour la demande entière. */
function avecNonJuges(recu: CanvasFormatSettingReceipt, nombre: number, raison: CanvasFormatUnreadableReason): CanvasFormatSettingReceipt {
  if (nombre === 0) return recu;
  return {
    ...recu,
    outcome: recu.outcome === 'different' ? 'different' : 'unreadable',
    unreadable: { count: (recu.unreadable?.count ?? 0) + nombre, reason: recu.unreadable?.reason ?? raison },
  };
}

/** Vrai si deux côtés montrent la même chose : aucun trait de part et d'autre, ou le même trait. */
export const memeCote = (a: TraitDeBordure | null, b: TraitDeBordure | null): boolean => (a === null || b === null ? a === b : memeTrait(a, b));

/**
 * Juge, réglage par réglage, ce que le fichier relu porte sur les cibles d'une
 * copie : conforme quand chaque cible jugée montre la valeur lue sur le modèle.
 *
 * @param relecture La forme relue dans le fichier exporté APRÈS l'écriture, ou
 *   la raison pour laquelle ce fichier ne juge pas l'écriture.
 * @param forme Ce que la copie a repris du modèle : les valeurs demandées.
 * @param cibles Les cibles entièrement écrites, ou qui montraient déjà la forme, dans l'ordre du document.
 * @param paragraphesNonEcrits Cibles dont une écriture au moins manque : non relues, comptées non jugées (`not_written`).
 * @returns Un reçu par réglage copié : le style nommé, les réglages de paragraphe, la bordure, les réglages de texte.
 */
export function verifierLaCopie(
  relecture: RelectureDeLActe,
  forme: FormeCopiee,
  cibles: readonly CibleCopiee[],
  paragraphesNonEcrits = 0,
): CanvasFormatSettingReceipt[] {
  /** Les cibles où `setting` est jugé, puis le reçu complété de celles où il n'a pas été écrit exprès. */
  const pour = (setting: CanvasFormatSettingKey, juger: (juges: readonly CibleCopiee[]) => CanvasFormatSettingReceipt | undefined): CanvasFormatSettingReceipt[] => {
    const recu = juger(cibles.filter((cible) => cible.nonEcrits[setting] === undefined));
    if (recu === undefined) return [];
    const parRaison = new Map<CanvasFormatUnreadableReason, number>();
    for (const cible of cibles) {
      const raison = cible.nonEcrits[setting];
      if (raison !== undefined) parRaison.set(raison, (parRaison.get(raison) ?? 0) + 1);
    }
    return [[...parRaison].reduce((complete, [raison, nombre]) => avecNonJuges(complete, nombre, raison), recu)];
  };

  const { styleId, traits } = forme;
  return [
    ...(styleId === undefined ? [] : pour('styleId', (juges) => jugerParCible('styleId', styleId, relecture, juges, paragraphesNonEcrits, (relu) => (
      relu.styleId === styleId ? { issue: 'conforme' } : { issue: 'different', lu: relu.styleId ?? 'none' }
    )))),
    ...REGLAGES_DE_PARAGRAPHE_COPIES.flatMap((setting) => {
      const demande = forme.paragraphe[setting];
      const { interligneEnPoints } = forme;
      // Un interligne en points ne se lit pas en multiple : il se juge sur ce que le fichier relu porte à la main.
      if (setting === 'lineSpacing' && interligneEnPoints !== undefined) {
        return pour(setting, (juges) => jugerParCible(setting, decrireLInterligneEnPoints(interligneEnPoints), relecture, juges, paragraphesNonEcrits, ({ aLaMain: { espacement: { line, lineRule } } }) => {
          if (line === interligneEnPoints.line && lineRule === interligneEnPoints.lineRule) return { issue: 'conforme' };
          return line === undefined || lineRule === undefined || lineRule === 'auto' ? { issue: 'non_lisible' } : { issue: 'different', lu: decrireLInterligneEnPoints({ line, lineRule }) };
        }));
      }
      return demande === undefined ? [] : pour(setting, (juges) => {
        const recu = verifierLaFormeDeParagraphe(relecture, juges, { [setting]: demande }, paragraphesNonEcrits)[0];
        if (recu?.outcome !== 'conforming') return recu;
        // Copie exacte : conforme à la tolérance du lecteur ne suffit pas. Là où le modèle et la cible posent le réglage
        // à la main, la valeur du fichier doit être la même.
        const auBrut = jugerParCible(setting, demande, relecture, juges, paragraphesNonEcrits, (relu) => {
          const ecart = ecartsAuBrut(forme.brut, relu.aLaMain).find((candidat) => candidat.setting === setting);
          return ecart === undefined ? { issue: 'conforme' } : { issue: 'different', lu: `${ecart.deLaCible} in the file (${ecart.attribut}), ${ecart.duModele} on the model` };
        });
        return auBrut.outcome === 'different' ? auBrut : recu;
      });
    }),
    ...(traits === undefined ? [] : pour('border', (juges) => jugerParCible('border', decrireLesTraits(traits), relecture, juges, paragraphesNonEcrits, (relu) => {
      const lus: Partial<Record<CanvasParagraphBorderSide, TraitDeBordure | null>> = {};
      for (const cote of CANVAS_PARAGRAPH_BORDER_SIDES) {
        const lu = relu.traitsDeBordure[cote];
        if (!lu.lisible) return { issue: 'non_lisible' };
        lus[cote] = lu.valeur;
      }
      const { top = null, left = null, bottom = null, right = null } = lus;
      return CANVAS_PARAGRAPH_BORDER_SIDES.every((cote) => memeCote(lus[cote] ?? null, traits[cote]))
        ? { issue: 'conforme' }
        : { issue: 'different', lu: decrireLesTraits({ top, left, bottom, right }) };
    }))),
    ...CANVAS_TEXT_FORMAT_SETTING_KEYS.flatMap((setting) => {
      const demande = forme.texte[setting];
      return demande === undefined ? [] : pour(setting, (juges) => (
        verifierLaFormeEcrite(relecture, juges.filter((cible) => cible.avecTexte).map(({ nodeId }) => ({ nodeId })), { [setting]: demande }, paragraphesNonEcrits)[0]
      ));
    }),
    // Toute la forme de caractère est celle du modèle : hors des sept réglages, la cible ne porte plus rien que l'éditeur sache retirer.
    ...(!copieUneFormeDeTexte(forme) ? [] : pour('otherTextFormatting', (juges) => jugerParCible('otherTextFormatting', 'none', relecture, juges.filter((cible) => cible.avecTexte), paragraphesNonEcrits, (relu) => {
      const restes = relu.formeDirecte.deTexte.filter((element) => !ELEMENTS_DES_SEPT.has(element) && REGLAGE_QUI_EFFACE[element] !== undefined);
      return restes.length === 0 ? { issue: 'conforme' } : { issue: 'different', lu: restes.join(', ') };
    }))),
  ];
}
